// Scheme-aware Authorization header construction.
//
// The app historically hard-coded `Authorization: Token ${token}` (DRF token).
// Switched / break-glass sessions (ENG-1401 / SN-10512) authenticate with a
// Bearer JWT, so the auth layer must be able to send either scheme. This module
// is the single place header values are built — every request site routes
// through it.
//
// IMPORTANT: keep this module free of `import.meta` (and anything that imports
// `VITE_*`) so ts-jest can unit-test it (see project CLAUDE.md gotcha).

export type AuthScheme = "Token" | "Bearer";

export const DEFAULT_AUTH_SCHEME: AuthScheme = "Token";

const SCHEME_KEY = "authScheme";

// The single Authorization-value builder. A `uid` selects the legacy `AToken`
// impersonation scheme, which is orthogonal to Token/Bearer — do NOT "fix" it
// to `Bearer ${uid}:${token}`; impersonation is always AToken.
export const buildAuthHeaderValue = (
  token: string,
  scheme: AuthScheme = DEFAULT_AUTH_SCHEME,
  uid?: string
): string => (uid ? `AToken ${uid}:${token}` : `${scheme} ${token}`);

// The Django Channels consumers (user/clinic notification sockets) expect a
// RAW DRF token key, or a JWT prefixed with "Bearer " — they branch on
// `token.startswith("Bearer ")`. This is DIFFERENT from the HTTP Authorization
// header: the Token scheme carries NO prefix here. After a practice switch the
// session becomes a Bearer JWT, so the socket auth message must prefix it or the
// consumer mis-routes it to DRF-token lookup and fails (ENG-1460).
export const buildWebsocketAuthToken = (
  token: string,
  scheme: AuthScheme = DEFAULT_AUTH_SCHEME
): string => (scheme === "Bearer" ? `Bearer ${token}` : token);

// Reads the persisted scheme, defaulting to Token. The default keeps existing
// logged-in users (whose storage has `token` but no `authScheme`) on the
// unchanged Token path.
export const getStoredAuthScheme = (): AuthScheme =>
  (localStorage.getItem(SCHEME_KEY) ||
    sessionStorage.getItem(SCHEME_KEY) ||
    DEFAULT_AUTH_SCHEME) as AuthScheme;

// For call sites without Redux access (module-level IIFEs, components that read
// the token straight from storage). Reads BOTH token and scheme from storage so
// a single header is never split across two stores.
export const getAuthHeaderFromStorage = (): string | null => {
  const token =
    localStorage.getItem("token") || sessionStorage.getItem("token");
  return token ? buildAuthHeaderValue(token, getStoredAuthScheme()) : null;
};

// Bare token for websocket first-message auth (scheme-agnostic transport).
export const stripAuthScheme = (raw: string): string =>
  raw.trim().replace(/^(AToken|Token|Bearer)\s+/i, "");

// Django sets a `csrftoken` cookie; its value is echoed back in the
// `X-CSRFToken` header on the Bearer refresh call.
export const getCsrfToken = (): string =>
  document.cookie.match(/(?:^|;\s*)csrftoken=([^;]+)/)?.[1] ?? "";

export type JwtPayload = Record<string, unknown>;

// Decodes a JWT's payload segment (base64url) without verifying the signature.
// Used to read non-sensitive session claims a Bearer token carries —
// `practice_id` (active), `home_practice_id`, `auth_method`, `break_glass` — to
// drive the practice context banner. Returns null for non-JWTs (e.g. DRF Token).
export const decodeJwtPayload = (token: string): JwtPayload | null => {
  try {
    const segment = token.split(".")[1];
    if (!segment) return null;
    const base64 = segment.replace(/-/g, "+").replace(/_/g, "/");
    const json = decodeURIComponent(
      atob(base64)
        .split("")
        .map((c) => "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2))
        .join("")
    );
    return JSON.parse(json) as JwtPayload;
  } catch {
    return null;
  }
};
